import {isPlatformBrowser, isPlatformServer} from '@angular/common';
import {inject, makeStateKey, PLATFORM_ID, TransferState} from '@angular/core';
import {EventType, NavigationCancel, NavigationCancellationCode, Router} from '@angular/router';
import {APP_ENVIRONMENT} from '@app/core/tokens/app-environment.token';
import {APP_SIGNALS} from '@app/core/tokens/app-signals.token';
import {UI_CONFIG} from '@app/core/tokens/ui-config.token';
import {ConfigurationResponse, InstanceUIConfigResponse, StatusService, UiConfigService} from '@middleware';
import {AppEnvironment} from '@models/environment';
import {lastValueFrom} from 'rxjs';

const UI_CONFIG_KEY = makeStateKey<InstanceUIConfigResponse>('uiConfigData');
const APP_CONFIG_KEY = makeStateKey<ConfigurationResponse>('appConfig');

async function fetchConfigurations(
  environment: AppEnvironment,
  statusService: StatusService,
  uiConfigService: UiConfigService,
): Promise<[ConfigurationResponse, InstanceUIConfigResponse]> {
  uiConfigService.baseUrl = environment.api;
  statusService.baseUrl = environment.api;

  return Promise.all([
    lastValueFrom(statusService.getConfigurations()),
    lastValueFrom(uiConfigService.getInstanceUIConfig()),
  ]);
}

/**
 * Attempts to read configurations from TransferState for client-side hydration.
 * @returns [appConfig, instanceUIConfig] if found, otherwise null.
 */
function attemptHydration(transferState: TransferState): [ConfigurationResponse, InstanceUIConfigResponse] | null {
  const appConfig = transferState.get(APP_CONFIG_KEY, null);
  const instanceUIConfig = transferState.get(UI_CONFIG_KEY, null);

  if (appConfig && instanceUIConfig) {
    transferState.remove(APP_CONFIG_KEY);
    transferState.remove(UI_CONFIG_KEY);
    return [appConfig, instanceUIConfig];
  }
  return null;
}

function stringifyError(error: unknown): string {
  let errorMessage: string = 'Unknown configuration fetch error.';

  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'object' && error !== null && 'message' in error) {
    const messageValue = (error as {message: unknown}).message;

    if (typeof messageValue === 'string') {
      errorMessage = messageValue;
    } else if (messageValue !== null && typeof messageValue === 'object') {
      try {
        errorMessage = JSON.stringify(messageValue);
      } catch (e) {
        errorMessage = 'Non-string error object message cannot be stringified.';
        console.error(errorMessage, e);
      }
    } else {
      errorMessage = String(messageValue);
    }
  } else {
    try {
      errorMessage = JSON.stringify(error);
    } catch (e) {
      console.error('Error during fallback JSON.stringify:', e);
    }
  }

  return errorMessage;
}

/**
 * Handles error logic and fallback if the API fetch fails.
 *
 * MODIFICATION: If not in CI/Bypass mode, we log the failure but still return [null, null]
 * instead of throwing, allowing SSR to finish. This relies on the environment variable
 * replacement script having already swapped the tokens in index.html.
 * @returns [appConfig, instanceUIConfig] (both null) if a fallback is used, otherwise throws.
 */
function handleErrorAndFallback(error: unknown, environment: AppEnvironment, isServer: boolean): [ConfigurationResponse, InstanceUIConfigResponse] {
  const errorMessage = stringifyError(error);
  const context = isServer ? 'SSR/Prerender' : 'Browser load';

  if (environment.bypassPrerenderApi) {
    // For Docker image builds in the context of GitHub Actions
    console.warn(`Failed to fetch configs during ${context}. Bypass flag is set, no tokens replaced.`, error);
    return [null, null];
  } else {
    // Log the error but DO NOT re-throw in non-local, non-CI environments.
    // Allow the application to load with default/placeholder values.
    console.error(
      `API call failed during ${context}: ${errorMessage}. Proceeding with application load, assuming placeholder replacement failed.`,
      error,
    );
    return [null, null];
  }
}

/**
 * Consolidates the initialization logic for both the server and browser sides.
 * It handles fetching critical application configurations and manages the hydration process
 * via TransferState.
 */
async function initializeConfigurations(platformId: Object, transferState: TransferState): Promise<[ConfigurationResponse, InstanceUIConfigResponse]> {
  const environment = inject(APP_ENVIRONMENT);
  const uiConfigService = inject(UiConfigService);
  const statusService = inject(StatusService);

  const isBrowser = isPlatformBrowser(platformId);
  const isServer = isPlatformServer(platformId);

  let appConfig: ConfigurationResponse;
  let instanceUIConfig: InstanceUIConfigResponse;

  if (isBrowser) {
    const hydratedData = attemptHydration(transferState);
    if (hydratedData) {
      return hydratedData;
    }
  }

  try {
    [appConfig, instanceUIConfig] = await fetchConfigurations(environment, statusService, uiConfigService);
  } catch (error) {
    // In CI builds, this path will execute handleErrorAndFallback, which returns [null, null].
    // In non-local, non-CI environments, it will now also return [null, null] instead of crashing the SSR process.
    [appConfig, instanceUIConfig] = handleErrorAndFallback(error, environment, isServer);
  }

  if (isServer) {
    // TransferState transfers data fetched on the server (SSR) into the HTML response.
    // The browser then reads this data on startup (hydration) instead of making
    // a redundant API call, ensuring better performance.
    transferState.set(APP_CONFIG_KEY, appConfig);
    transferState.set(UI_CONFIG_KEY, instanceUIConfig);
  }

  return [appConfig, instanceUIConfig];
}

/**
 * The application initializer function, run before the main Angular application starts.
 * * This solves two key problems:
 * 1. Critical Data Blocking: Ensures essential global configurations (API URLs, UI settings)
 * are available synchronously for services before they are instantiated.
 * 2. SSR Hydration: Manages data transfer between the Server (Node.js) and the Browser (client)
 * using TransferState. TransferState prevents the browser from refetching data that
 * was already retrieved during the SSR phase, improving performance and SEO.
 */
export async function appInitializer(): Promise<void> {
  const router = inject(Router);
  const uiConfigSignal = inject(UI_CONFIG);
  const appSignals = inject(APP_SIGNALS);
  const transferState = inject(TransferState);
  const platformId = inject(PLATFORM_ID);

  let appConfig: ConfigurationResponse;
  let instanceUIConfig: InstanceUIConfigResponse;

  [appConfig, instanceUIConfig] = await initializeConfigurations(platformId, transferState);

  // If appConfig or instanceUIConfig are null (due to API failure),
  // the signals will be set to null, causing them to use their computed fallbacks.
  appSignals.appConfig.set(appConfig);
  uiConfigSignal.set(instanceUIConfig);

  router.events.subscribe(event => {
    if (
      event.type == EventType.NavigationCancel &&
      event.id == 1 &&
      event instanceof NavigationCancel &&
      event.code != NavigationCancellationCode.Redirect &&
      event.code != NavigationCancellationCode.SupersededByNewNavigation
    ) {
      // The very first navigation failed and doesn't appear to be taking the user anywhere new.
      // This will probably show a blank page, which we want to avoid.
      router.navigateByUrl('/', {onSameUrlNavigation: 'reload'});
    }
  });
}
